import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import HardwareRoundedIcon from "@mui/icons-material/HardwareRounded";
import HardwareOutlinedIcon from "@mui/icons-material/HardwareOutlined";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import colors from "../../Constants/colors";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { useTranslation } from "react-i18next";
import {
  useGradingCriteria,
  useDeleteGradingCriterion,
  useSetDefaultGradingCriterion,
  useGradingCriterion,
} from "../../Query/useGradingCriteria";
import GradingCriterionModal from "./GradingCriterionModal";

const GradingCriteriaAllPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("gradingCriteria");
  const { colors: workspace } = useWorkspaceColors();
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data, isLoading, isError } = useGradingCriteria();
  const { mutate: deleteItem } = useDeleteGradingCriterion();
  const { mutate: setDefault } = useSetDefaultGradingCriterion();

  const { data: selectedItem, isLoading: isSingleLoading } =
    useGradingCriterion(selectedId ?? 0);

  const handleDelete = (confirmed: boolean) => {
    setConfirmationDialogOpen(false);
    if (confirmed && selectedId) {
      deleteItem(selectedId);
    }
  };

  const handleRowClick = (id: number) => {
    setSelectedId(id);
    setIsModalOpen(true);
  };

  if (isLoading) {
    return (
      <Loading
        backgroundColor={colors.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 4, color: colors.red700 }}>
        Error: Failed to fetch grading criteria
      </Box>
    );
  }

  const headerCellSx = {
    fontWeight: 600,
    color: colors.gray700,
    backgroundColor: colors.gray50,
    borderBottom: `1px solid ${colors.gray200}`,
    fontSize: "0.8rem",
  };

  const bodyCellSx = {
    color: colors.primary_dark,
    fontSize: "0.875rem",
    borderBottom: `1px solid ${colors.gray100}`,
  };

  return (
    <Box
      sx={{ minHeight: "calc(100vh - 64px)", backgroundColor: colors.gray50 }}
    >
      <Box sx={{ maxWidth: "lg", mx: "auto", px: 3, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <HardwareRoundedIcon
                sx={{ fontSize: 32, color: workspace.primary_dark }}
              />
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: workspace.primary_dark }}
                >
                  {t("title")}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.gray500 }}>
                  {data?.length ?? 0}
                </Typography>
              </Box>
            </Stack>

            <IconButton
              onClick={() => navigate("/grading_criterion/create")}
              sx={{
                bgcolor: workspace.accent,
                color: workspace.primary_dark,
                borderRadius: 1.5,
                px: 1.5,
                py: 1,
                gap: 0.75,
                "&:hover": { bgcolor: workspace.accent, opacity: 0.9 },
              }}
            >
              <AddCircleOutlineIcon sx={{ fontSize: 20 }} />
              <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                {t("create")}
              </Typography>
            </IconButton>
          </Stack>
        </Paper>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            background: colors.white,
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: "70%" }}>
                    {t("name")}
                  </TableCell>
                  <TableCell
                    sx={{ ...headerCellSx, width: "30%" }}
                    align="right"
                  >
                    {t("actions")}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {(data ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      align="center"
                      sx={{
                        py: 6,
                        color: colors.gray500,
                        backgroundColor: colors.gray50,
                      }}
                    >
                      {t("noGradingCriteria")}
                    </TableCell>
                  </TableRow>
                ) : (
                  (data ?? []).map((item) => (
                    <TableRow
                      key={item.id}
                      onClick={() => handleRowClick(item.id)}
                      sx={{
                        cursor: "pointer",
                        transition: "0.15s",
                        "&:hover": {
                          backgroundColor: `${workspace.primary_dark}08`,
                        },
                      }}
                    >
                      <TableCell sx={bodyCellSx}>{item.name}</TableCell>

                      <TableCell sx={bodyCellSx} align="right">
                        <Tooltip title={t("setDefault")}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDefault(item.id);
                            }}
                            sx={{
                              color: item.default
                                ? workspace.accent
                                : colors.gray400,
                            }}
                          >
                            {item.default ? (
                              <HardwareRoundedIcon fontSize="small" />
                            ) : (
                              <HardwareOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={t("edit")}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/grading_criterion/edit/${item.id}`);
                            }}
                            sx={{ color: colors.gray600 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={t("delete")}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedId(item.id);
                              setConfirmationDialogOpen(true);
                            }}
                            sx={{ color: colors.red600 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <GradingCriterionModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        gradingCriterion={selectedItem ?? null}
        isLoading={isSingleLoading}
      />

      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={handleDelete}
        title={t("deleteTitle")}
        description={t("deleteDescription")}
        content={t("deleteContent")}
        actions={[
          { label: t("no"), onClick: () => handleDelete(false) },
          { label: t("yes"), onClick: () => handleDelete(true) },
        ]}
      />
    </Box>
  );
};

export default GradingCriteriaAllPage;
